import React from 'react';
import wingitDesign from '../designSystem';

export default function DashboardMainContent({
  isMobile,
  isOwner,
  statusRows,
  operations,
  operationsLoading,
  operationsError,
  getStatusAppearance,
  operationTone,
  navigate,
  profileIncomplete,
}) {
  const styles = getStyles(isMobile);

  function renderPilotStatus() {
    if (statusRows.length === 0) {
      return (
        <div style={styles.emptyState}>
          <div style={{ marginBottom: '0.75rem' }}>
            Complete your profile to activate pilot
            status tracking.
          </div>

          <button
            type="button"
            onClick={() => navigate('/profile')}
            style={styles.profileButton}
          >
            Complete Profile
          </button>
        </div>
      );
    }

    return (
      <div style={styles.statusGrid}>
        {statusRows.slice(0, 4).map((row) => {
          const appearance =
            getStatusAppearance(row.status);

          const summary = formatStatusSummary(
            row.label,
            row.summary
          );

          return (
            <div
              key={row.key}
              style={{
                ...styles.statusTile,
                borderColor:
                  'rgba(255,255,255,0.08)',
                background:
                  'rgba(12,22,36,0.55)',
              }}
            >
              <div style={styles.statusTileHeader}>
                <div style={styles.tileLabel}>
                  {formatStatusLabel(row.label)}
                </div>

                <div
                  style={{
                    ...styles.statusChip,
                    color: appearance.color,
                    borderColor: appearance.border,
                    background:
                      appearance.background,
                  }}
                >
                  {formatStatusChip(
                    appearance.label
                  )}
                </div>
              </div>

              <div style={styles.tileSummary}>
                {summary}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  function openOutstandingTechLog() {
    const row =
      operations.techOutstanding?.[0];

    if (!row) {
      navigate('/aircraft-tech-log');
      return;
    }

    const search = new URLSearchParams({
      aircraftId: String(
        row.aircraft_id || ''
      ),
      bookingId: String(row.id || ''),
      from: 'dashboard',
    });

    navigate(
      `/aircraft-tech-log?${search.toString()}`
    );
  }

  function openOwnerDocuments() {
    navigate('/admin#admin-documents');
  }

  function renderOperationsSummary() {
    if (!isOwner) return null;

    const summaryItems = [
      {
        label: 'Open Defects',
        value: operations.openDefects.length,
        helper:
          operations.openDefects.length > 0
            ? 'Review aircraft defects'
            : 'No defects awaiting action',
        tone: 'danger',
        onClick: () =>
          navigate(
            '/admin?section=operations&view=defects'
          ),
      },
      {
        label: 'Tech Logs',
        value: operations.techOutstanding.length,
        helper:
          operations.techOutstanding.length > 0
            ? 'Open outstanding tech logs'
                        : 'No tech logs awaiting action',
        tone: 'warning',
        onClick: openOutstandingTechLog,

      },
      {
        label: 'Payments',
        value:
          operations.paymentOutstanding.length,
        helper:
          operations.paymentOutstanding.length > 0
            ? 'Review outstanding payments'
            : 'No payments awaiting action',
        tone: 'warning',
        onClick: () =>
          navigate(
            '/admin?section=operations&view=payments'
          ),
      },
      {
        label: 'Documents',
        value:
          operations.documentAttention.length,
        helper:
          operations.documentAttention.length > 0
            ? 'Open document alerts'
            : 'No documents require attention',
        tone:
          operations.documentAttention.length > 0
            ? 'warning'
            : 'neutral',
        onClick: openOwnerDocuments,
        alwaysAvailable: true,
      },
    ];

    return (
      <section style={styles.panel}>
        <div style={styles.sectionHeader}>
          <div>
            <div style={styles.eyebrow}>
              OWNER
            </div>

            <h3 style={styles.sectionTitle}>
              Club Operations
            </h3>

            <p style={styles.sectionSubtle}>
              Operational items needing attention.
            </p>
          </div>
        </div>

        {operationsLoading ? (
          <div style={styles.emptyState}>
            Loading operations...
          </div>
        ) : operationsError ? (
          <div style={styles.errorBox}>
            {operationsError}
          </div>
        ) : (
          <div style={styles.operationsGrid}>
            {summaryItems.map((item) => {
              const tone =
                operationTone[item.tone] ||
                operationTone.neutral;

              const hasAction =
                item.alwaysAvailable ||
                Number(item.value) > 0;

              return (
                <button
                  key={item.label}
                  type="button"
                  onClick={
                    hasAction
                      ? item.onClick
                      : undefined
                  }
                  disabled={!hasAction}
                  style={{
                    ...styles.operationTile,
                    borderColor: tone.border,
                    background: tone.background,
                    opacity: hasAction ? 1 : 0.72,
                    cursor: hasAction
                      ? 'pointer'
                      : 'default',
                  }}
                >
                  <div
                    style={styles.operationLabel}
                  >
                    {item.label}
                  </div>

                  <div
                    style={{
                      ...styles.operationValue,
                      color: tone.color,
                    }}
                  >
                    {item.value}
                  </div>

                  <div
                    style={styles.operationHelper}
                  >
                    {item.helper}
                  </div>

                  {hasAction ? (
                    <div
                      style={styles.operationAction}
                    >
                      Open →
                    </div>
                  ) : null}
                </button>
              );
            })}

            <button
              type="button"
              onClick={() => navigate('/admin')}
              style={styles.adminButton}
            >
              Open Admin Settings
            </button>
          </div>
        )}
      </section>
    );
  }

  return (
    <div
      style={
        isOwner
          ? styles.supportGridOwner
          : styles.supportGrid
      }
    >
      <section style={styles.panel}>
        <div style={styles.sectionHeader}>
          <div>
            <div style={styles.eyebrow}>
              PILOT
            </div>

            <h3 style={styles.sectionTitle}>
              {isOwner
                ? 'Your Pilot Status'
                : 'Pilot Status'}
            </h3>

            <p style={styles.sectionSubtle}>
              Licence, medical and currency at a
              glance.
            </p>
          </div>

          <button
            type="button"
            onClick={() => navigate('/profile')}
            style={styles.profileButton}
          >
            Edit Profile
          </button>
        </div>

        {profileIncomplete && (
          <div style={styles.onboardingBox}>
            <div>
              <div style={styles.onboardingTitle}>
                Complete your pilot profile
              </div>

              <div style={styles.onboardingText}>
                Before your first flight, add your
                phone number, licence details, home
                airfield, licence expiry and medical
                expiry.
              </div>
            </div>

            <button
              type="button"
              onClick={() => navigate('/profile')}
              style={styles.profileButton}
            >
              Complete Profile
            </button>
          </div>
        )}

        {renderPilotStatus()}
      </section>

      {renderOperationsSummary()}
    </div>
  );
}

function formatStatusLabel(label) {
  const text = String(label || '').trim();

  if (!text) return 'Status';

  return text
    .toLowerCase()
    .replace(
      /\b\w/g,
      (char) => char.toUpperCase()
    );
}

function formatStatusChip(label) {
  const text = String(label || '')
    .trim()
    .toLowerCase();

  if (text === 'current') return 'Current';
  if (text === 'due soon') return 'Due soon';
  if (text === 'action required') return 'Action';

  return label;
}

function formatStatusSummary(label, summary) {
  const cleanLabel = String(label || '')
    .trim()
    .toLowerCase();

  const cleanSummary = String(
    summary || ''
  ).trim();

  if (!cleanSummary) {
    return 'Status not recorded';
  }

  if (
    cleanLabel.includes('club currency') &&
    cleanSummary === 'No landing data'
  ) {
    return 'No recorded landings';
  }

  if (
    cleanLabel.includes('night') &&
    cleanSummary === 'No night record'
  ) {
    return 'No night flights recorded';
  }

  if (
    cleanLabel.includes('licence') &&
    /expires in/i.test(cleanSummary)
  ) {
    return cleanSummary.replace(
      /expires in\s*/i,
      'Licence expires in '
    );
  }

  if (
    cleanLabel.includes('medical') &&
    /expires in/i.test(cleanSummary)
  ) {
    return cleanSummary.replace(
      /expires in\s*/i,
      'Medical expires in '
    );
  }

  if (
    /expires in \(s\)/i.test(cleanSummary)
  ) {
    return cleanSummary.replace(
      /expires in \(s\)/i,
      'Expires in'
    );
  }

  return cleanSummary;
}

function getStyles(isMobile) {
  const ds = wingitDesign;

  return {
    supportGridOwner: {
      gridColumn: '1 / -1',
      display: 'grid',
      gridTemplateColumns: isMobile
        ? '1fr'
        : '1.1fr 1fr',
      gap: isMobile ? '0.75rem' : '1rem',
      alignItems: 'stretch',
    },

    supportGrid: {
      gridColumn: '1 / -1',
      display: 'grid',
      gridTemplateColumns: '1fr',
      gap: isMobile ? '0.75rem' : '1rem',
      alignItems: 'stretch',
    },

    panel: {
      ...ds.cards.secondary,
      borderRadius: ds.radius.large,
      padding: isMobile
        ? '1rem'
        : '1.15rem',
      boxShadow: ds.shadow.card,
      transition: ds.transition.normal,
    },

    sectionHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: '1rem',
      flexWrap: 'wrap',
      marginBottom: '0.9rem',
    },

    eyebrow: {
      ...ds.typography.caption,
      color: ds.colors.primary,
      fontWeight: 900,
      marginBottom: '0.28rem',
    },

    sectionTitle: {
      margin: 0,
      color: ds.colors.text,
      fontSize: isMobile
        ? '1rem'
        : '1.18rem',
      letterSpacing: '-0.02em',
      fontWeight: 850,
    },

    sectionSubtle: {
      ...ds.typography.body,
      margin: '0.28rem 0 0',
      color: ds.colors.textMuted,
      fontSize: isMobile
        ? '0.82rem'
        : '0.88rem',
      lineHeight: 1.35,
    },

    statusGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile
        ? '1fr'
        : 'repeat(2, minmax(0, 1fr))',
      gap: '0.7rem',
    },

    statusTile: {
      border: '1px solid',
      borderRadius: ds.radius.medium,
      padding: '0.85rem',
      minHeight: 118,
      boxShadow:
        'inset 0 1px 0 rgba(255,255,255,0.04)',
    },

    statusTileHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '0.7rem',
      marginBottom: '0.75rem',
    },

    tileLabel: {
      color: ds.colors.text,
      fontSize: '0.95rem',
      fontWeight: 650,
      letterSpacing: '-0.01em',
    },

    statusChip: {
      border: '1px solid',
      borderRadius: 999,
      padding: '0.24rem 0.5rem',
      fontSize: '0.68rem',
      fontWeight: 800,
      whiteSpace: 'nowrap',
    },

    tileSummary: {
      color: ds.colors.text,
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },

    operationsGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile
        ? '1fr'
        : 'repeat(4, minmax(0, 1fr))',
      gap: '0.7rem',
    },

    operationTile: {
      border: '1px solid',
      borderRadius: ds.radius.medium,
      padding: '0.85rem',
      minHeight: 128,
      textAlign: 'left',
      width: '100%',
      color: ds.colors.text,
      transition: ds.transition.normal,
      boxShadow:
        'inset 0 1px 0 rgba(255,255,255,0.04)',
    },

    operationLabel: {
      color: ds.colors.text,
      fontSize: '0.72rem',
      fontWeight: 950,
      letterSpacing: '0.13em',
      textTransform: 'uppercase',
    },

    operationValue: {
      fontSize: isMobile
        ? '1.55rem'
        : '1.9rem',
      fontWeight: 950,
      lineHeight: 1,
      marginTop: '0.55rem',
    },

    operationHelper: {
      color: ds.colors.textMuted,
      fontSize: '0.76rem',
      marginTop: '0.5rem',
      lineHeight: 1.35,
    },

    operationAction: {
      color: ds.colors.primary,
      fontSize: '0.72rem',
      fontWeight: 800,
      marginTop: '0.65rem',
    },

    adminButton: {
      ...ds.buttons.secondary,
      gridColumn: isMobile
        ? 'auto'
        : '1 / -1',
      marginTop: '0.1rem',
      fontSize: '0.82rem',
      width: '100%',
      padding: '0.72rem 1rem',
    },

    onboardingBox: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '1rem',
      flexWrap: 'wrap',
      marginBottom: '0.9rem',
      background:
        'rgba(30,207,255,0.08)',
      border: `1px solid ${ds.colors.border}`,
      borderRadius: ds.radius.medium,
      padding: '0.9rem',
    },

    onboardingTitle: {
      color: ds.colors.text,
      fontSize: '0.95rem',
      fontWeight: 750,
      marginBottom: '0.25rem',
    },

    onboardingText: {
      color: ds.colors.textMuted,
      fontSize: '0.86rem',
      lineHeight: 1.4,
    },

    profileButton: {
      ...ds.buttons.primary,
      fontSize: '0.82rem',
      padding: '0.72rem 1rem',
    },

    emptyState: {
      color: ds.colors.textMuted,
      fontSize: '0.9rem',
      lineHeight: 1.4,
      background:
        'rgba(150,176,207,0.06)',
      border: `1px solid ${ds.colors.border}`,
      borderRadius: ds.radius.medium,
      padding: '0.9rem',
    },

    errorBox: {
      ...ds.chips.danger,
      borderRadius: ds.radius.medium,
      padding: '0.8rem',
      fontSize: '0.9rem',
    },
  };
}